import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type BillingOverview = Schemas['Overview'];
export type BillingDetails = Schemas['BillingDetails'];
export type BillingInvoice = Schemas['BillingInvoice'];
export type PublicPlan = Schemas['PlanDto'];

const key = (ws: string) => ['workspace', ws, 'billing'] as const;
const wsPath = (ws: string) => ({ params: { path: { workspaceId: ws } } });

export function useBilling(ws: string) {
  return useQuery({
    queryKey: key(ws),
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/billing', wsPath(ws))),
  });
}

export function useBillingInvoices(ws: string) {
  return useQuery({
    queryKey: [...key(ws), 'invoices'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/billing/invoices', wsPath(ws))),
  });
}

export function useSaveBillingDetails(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BillingDetails) =>
      unwrap(api.PUT('/api/v1/workspaces/{workspaceId}/billing/details', { ...wsPath(ws), body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(ws) }),
  });
}

export function useCheckout(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['CheckoutRequest']) =>
      unwrap(api.POST('/api/v1/workspaces/{workspaceId}/billing/checkout', { ...wsPath(ws), body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws] }),
  });
}

export function useResumeSubscription(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/workspaces/{workspaceId}/billing/resume', wsPath(ws))),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(ws) }),
  });
}
